import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Divider } from 'antd';
import { useParams, withRouter } from 'react-router-dom';
import { getTrainingList, getTraining, getTrainingListStatus } from '../../../lib/api/training-api';
import { useInterval } from '../../../lib/useInterval';
import ModelHeader from './ModelHeader';
import VersionHistory from './VersionHistory';
import _ from 'lodash';
import { Training } from '../../../types';
import { PageHeader } from '../../../components/layout/PageHeader';
import { useGetAlgorithm, useGetModel } from '../../../lib/hooks';

type Props = {};

const ModelDetail = (props: Props) => {
  const { modelId, algorithmId } = useParams() as { modelId: string; algorithmId: string };

  const { model } = useGetModel(modelId);
  const { algorithm } = useGetAlgorithm(algorithmId);
  const [trainingList, setTrainingList] = useState<null | Training[]>(null);

  const fetchTrainingList = useCallback(
    async model => {
      const trainingList = await getTrainingList(model.id);
      setTrainingList(trainingList.filter(training => training.status !== 'CREATED'));
    },
    [setTrainingList]
  );

  useEffect(() => {
    if (model) {
      fetchTrainingList(model);
    }
  }, [model, fetchTrainingList]);

  useInterval(async () => {
    try {
      if (trainingList && trainingList.filter(training => training.status === 'TRAINING').length > 0) {
        updateTrainingListStatus();
      }
    } catch (e) {}
  }, 2000);

  const updateTrainingListStatus = async () => {
    if (!trainingList || !model) return;

    const trainingListStatus = await getTrainingListStatus(model.id);

    const newData = _.cloneDeep(trainingList);

    await Promise.all(
      trainingList.map(async (training, index) => {
        if (training.status !== trainingListStatus[training.id]) {
          const trainingFromBackend = await getTraining(training.id);
          newData[index] = {
            ...training,
            status: trainingListStatus[training.id],
            resultModelVersionId: trainingFromBackend.resultModelVersionId,
          };
        }
      })
    );

    setTrainingList(newData);
  };

  const onItemClickUrl = useCallback((trainingId: string) => {
    return `/custom/${trainingId}/result?readOnly=true`;
  }, []);

  return (
    <>
      <PageHeader
        back={{ url: `/custom/${algorithm && algorithm.id}/models`, label: 'Customization' }}
        header={model && model.name}
        description="Here you can see and test different versions of this model."
      />
      {model && algorithm && (
        <>
          <Row>
            <ModelHeader
              algorithm={algorithm}
              model={model}
              mostRecentTraining={
                (trainingList && trainingList.length > 0 && trainingList[trainingList.length - 1]) || null
              }
              trainingCount={trainingList ? trainingList.length : null}
            />
            <Divider />
          </Row>
          <Row>
            {trainingList && (
              <Col span={24}>
                <VersionHistory
                  trainingList={trainingList}
                  onItemClickUrl={onItemClickUrl}
                  model={model}
                  algorithm={algorithm}
                />
              </Col>
            )}
          </Row>
        </>
      )}
    </>
  );
};

export default withRouter(ModelDetail);
